import { useId } from 'react';
import {
  Box,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ApiErrorState } from '@/components/ApiErrorState';
import { StatusChip } from '@/components/status/StatusChip';
import { formatCivilOrDash } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useSeats } from './useSeats';

export interface SubscriberSeatsProps {
  sponsorUserId: number;
}

export function SubscriberSeats({ sponsorUserId }: SubscriberSeatsProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const seats = useSeats(sponsorUserId);
  const headingId = useId();
  const unavailable = t('subscribers.unavailable');

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h3" component="h3" id={headingId}>
        {t('subscribers.seats.title')}
      </Typography>
      {seats.status === 'loading' ? (
        <Box role="status" aria-label={t('common.loading')} sx={{ mt: 1 }}>
          <Skeleton variant="rounded" height={120} />
        </Box>
      ) : null}
      {seats.status === 'error' ? (
        <Box sx={{ mt: 1 }}>
          <ApiErrorState error={seats.error} onRetry={seats.reload} />
        </Box>
      ) : null}
      {seats.status === 'ready' && seats.seats.length === 0 ? (
        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
          {t('subscribers.seats.none')}
        </Typography>
      ) : null}
      {seats.status === 'ready' && seats.seats.length > 0 ? (
        <Paper sx={{ overflow: 'hidden', mt: 1 }}>
          <Table size="small" aria-labelledby={headingId}>
            <TableHead>
              <TableRow>
                <TableCell>{t('subscribers.seats.guest')}</TableCell>
                <TableCell>{t('subscribers.seats.status')}</TableCell>
                <TableCell>{t('subscribers.seats.since')}</TableCell>
                <TableCell>{t('subscribers.seats.ends')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {seats.seats.map((seat) => (
                <TableRow key={seat.entitlementId}>
                  <TableCell component="th" scope="row">
                    {seat.guestDisplayName ?? unavailable}
                  </TableCell>
                  <TableCell>
                    <StatusChip domain="seat" status={seat.status} />
                  </TableCell>
                  <TableCell>{formatCivilOrDash(seat.since, locale, unavailable)}</TableCell>
                  <TableCell>{formatCivilOrDash(seat.endsAt, locale, unavailable)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      ) : null}
    </Box>
  );
}
